import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiTrendingUp, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../../styles/UpsellSummaryView.css';
import UpsellIcon from '../../assets/images/Upsell Indicator 1.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';


const POLICY_PAGE_SIZE  = 10;

const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

interface Client {
  name: string;
  databaseId: string;
  eMail?: string;
  insuredType?: number;
}

interface Policy {
  databaseId: string;
  title: string;
  effectiveDate: string;
  expirationDate: string;
  policyNumber: string;
  totalPremium: number;
  linesOfBusiness: string[];
}

interface SummaryData {
  summary: string;
  upsell_indicator?: string;
  overall_recommendation?: string;
}



const formatDate = (iso: string) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  } catch { return iso; }
};

const getLobEndpoint = (lob: string[] | undefined): string => {
  const base = `${(process.env.REACT_APP_PolicyAgent_Url  || '')}/api/policy-upsell-summary`;
  if (!lob || lob.length === 0) return `${base}/commercial/streaming`;
  const l = lob[0].toLowerCase();
  if (l.includes('commercial'))                  return `${base}/commercial/streaming`;
  if (l.includes('home') || l.includes('property')) return `${base}/homeowners/streaming`;
  if (l.includes('auto'))                        return `${base}/personal-auto/streaming`;
  return `${base}/commercial/streaming`;
};

const mdTableComponents = {
  table: ({ children }: any) => (
    <div className="upsell-summary__md-table-wrap">
      <table className="upsell-summary__md-table">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="upsell-summary__md-thead">{children}</thead>
  ),
  th: ({ children }: any) => (
    <th className="upsell-summary__md-th">
      {children}
    </th>
  ),
  tr: ({ children }: any) => (
    <tr className="upsell-summary__md-tr">{children}</tr>
  ),
  td: ({ children }: any) => (
    <td className="upsell-summary__md-td">
      {children}
    </td>
  ),
};

const UpsellSummaryView: React.FC = () => {
  const navigate = useNavigate();

  /* Client search */
  const [query,         setQuery]         = useState('');
  const [clientResults, setClientResults] = useState<Client[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Policies */
  const [policies,        setPolicies]        = useState<Policy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policyPage,      setPolicyPage]      = useState(1);

  /* Summary dialog */
  const [showDialog,     setShowDialog]     = useState(false);
  const [dialogLoading,  setDialogLoading]  = useState(false);
  const [isStreaming,    setIsStreaming]     = useState(false);
  const [streamText,     setStreamText]     = useState('');
  const [summaryData,    setSummaryData]    = useState<SummaryData | null>(null);
  const [summaryId,      setSummaryId]      = useState<number | null>(null);
  const [activePolicy,   setActivePolicy]   = useState<Policy | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const abortRef   = useRef<AbortController | null>(null);

  const { showSuccess } = useToast();

  /* Feedback */
  const feedbackRef        = useRef<HTMLDivElement>(null);
  const [feedbackText,     setFeedbackText]     = useState('');
  const [feedbackRating,   setFeedbackRating]   = useState<boolean | null>(null);
  const [feedbackAnchor,   setFeedbackAnchor]   = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError,    setFeedBackError]    = useState('');

  /* Close feedback on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        setFeedbackAnchor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Auto-scroll dialog content while streaming */
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamText]);

  /* Debounced client search */
  useEffect(() => {
    if (!query.trim()) { setClientResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchClients(query), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const searchClients = async (q: string) => {
    setClientLoading(true);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI     || '')}/api/nowcert/customerlist`, {
        params: { searchText: q, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setClientResults(res.data || []);
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setClientLoading(false);
    }
  };

  const onSelectClient = async (client: Client) => {
    setSelectedClient(client);
    setQuery('');
    setClientResults([]);
    setPolicies([]);
    setPolicyPage(1);
    await fetchPolicies(client.databaseId);
  };

  const fetchPolicies = async (clientId: string) => {
    setPoliciesLoading(true);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI     || '')}/api/nowcert/insuredpolicies`, {
        params: { insuredId: clientId, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data: any[] = res.data || [];
      const transformed: Policy[] = data.map(item => ({
        databaseId:      item.databaseId || item.id || '',
        title:           item.linesOfBusiness?.[0] || 'Homeowners',
        effectiveDate:   item.effectiveDate  || '',
        expirationDate:  item.expirationDate || '',
        policyNumber:    item.policyNumber   || 'N/A',
        totalPremium:    item.totalPremium   || 0,
        linesOfBusiness: Array.isArray(item.linesOfBusiness)
          ? item.linesOfBusiness
          : item.linesOfBusiness ? [item.linesOfBusiness] : [],
      }));
      setPolicies(transformed);
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setPoliciesLoading(false);
    }
  };

  const openSummary = (policy: Policy) => {
    // Cancel any in-flight stream
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setActivePolicy(policy);
    setShowDialog(true);
    setDialogLoading(true);
    setIsStreaming(true);
    setStreamText('');
    setSummaryData(null);
    setSummaryId(null);
    streamSummary(policy, abortRef.current.signal);
  };

  const streamSummary = async (policy: Policy, signal: AbortSignal) => {
    const url     = getLobEndpoint(policy.linesOfBusiness);
    const formData = new FormData();
    formData.append('policy_database_id', policy.databaseId);

    let localSummary = '';

    try {
      const res = await fetch(url, {
        method:  'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body:    formData,
        signal,
      });

      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      setDialogLoading(false);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') { setIsStreaming(false); return; }

          try {
            const parsed = JSON.parse(raw);
            if (parsed.id) setSummaryId(parsed.id);

            if (parsed.summary) {
              let chunk: string = parsed.summary;
              if (chunk.includes('$$POLICY_UPSELL_INDICATOR')) {
                chunk = chunk.split('$$POLICY_UPSELL_INDICATOR')[0];
              }
              chunk = chunk.replace(/\$\$POLICY_UPSELL_INDICATOR/g, '');
              localSummary += chunk;
              setStreamText(t => t + chunk);
              setSummaryData(prev => ({ ...prev!, summary: (prev?.summary || '') + chunk }));
            }

            if (parsed.upsell_indicator) {
              setSummaryData(prev => ({ ...prev!, upsell_indicator: parsed.upsell_indicator }));
            }

            if (parsed.overall_recommendation) {
              setSummaryData(prev => ({ ...prev!, overall_recommendation: parsed.overall_recommendation }));
            }
          } catch {
            let chunk = raw;
            if (chunk.includes('$$POLICY_UPSELL_INDICATOR')) {
              chunk = chunk.split('$$POLICY_UPSELL_INDICATOR')[0];
            }
            chunk = chunk.replace(/\$\$POLICY_UPSELL_INDICATOR/g, '');
            if (chunk) {
              localSummary += chunk;
              setStreamText(t => t + chunk);
              setSummaryData(prev => ({ ...prev!, summary: (prev?.summary || '') + chunk }));
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (err.status === 401 || err.message?.includes('401')) { navigate('/login'); return; }
    } finally {
      setIsStreaming(false);
      setDialogLoading(false);
    }
  };

  const closeFeedback = () => { setFeedbackAnchor(null); };

  const closeDialog = () => {
    abortRef.current?.abort();
    setShowDialog(false);
    closeFeedback();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  };

  /* Feedback */
  const openFeedback = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  };

  const saveRating = (isLiked: boolean | null) => {
    axios.post(`${(process.env.REACT_APP_DataFabric_Url   || '')}/api/NCAICommissionStatement/feedback`, {
      Target: 8, TransactionId: String(summaryId ?? 0),
      IsLiked: isLiked, Feedback: feedbackText ?? '',
    }, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch((err: any) => { if (err.response?.status === 401) navigate('/login'); });
  };

  const onThumbsUpClick = () => {
    const next = feedbackRating === true ? null : true;
    closeFeedback();
    saveRating(next);
  };

  const onThumbsDownClick = () => {
    const next = feedbackRating === false ? null : false;
    closeFeedback();
    saveRating(next);
  };

  const onSubmitFeedback = async () => {
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Feedback must be under 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    try {
      await axios.post(`${(process.env.REACT_APP_DataFabric_Url   || '')}/api/NCAICommissionStatement/feedback`, {
        Target: 8, TransactionId: String(summaryId ?? 0),
        IsLiked: feedbackRating, Feedback: feedbackText,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setFeedbackSubmitting(false);
      showSuccess('Feedback submitted successfully!');
      closeFeedback();
    } catch (err: any) {
      setFeedbackSubmitting(false);
      if (err.response?.status === 401) navigate('/login');
    }
  };

  /* Column templates */

  return (
    <div className="upsell-summary__root">
      {/* Header */}
      <div>
        <h2 className="upsell-summary__heading">
          Upsell Indicator
        </h2>
        <p className="upsell-summary__subtitle">
          AI-powered upsell insights based on customer and policy data
        </p>
      </div>

      {/* Client search */}
      <div className="cs-client-search">
        <div className="cs-client-search__input-wrap">
          <FiSearch size={15} className="cs-client-search__icon" />
          <input
            className="cs-client-search__input"
            type="text"
            placeholder={selectedClient ? selectedClient.name : 'Search client name…'}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              if (selectedClient) setSelectedClient(null);
            }}
          />
          {clientLoading && <span className="cs-client-search__spinner" />}
        </div>

        {clientResults.length > 0 && (
          <ul className="cs-client-dropdown">
            {clientResults.map(c => (
              <li
                key={c.databaseId}
                className="cs-client-dropdown__item"
                onClick={() => onSelectClient(c)}
              >
                <span className="cs-client-dropdown__name">{c.name}</span>
                {c.eMail && (
                  <span className="cs-client-dropdown__email">{c.eMail}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Policy table */}
      {selectedClient && (() => {
        const totalPages = Math.max(1, Math.ceil(policies.length / POLICY_PAGE_SIZE));
        const paged      = policies.slice((policyPage - 1) * POLICY_PAGE_SIZE, policyPage * POLICY_PAGE_SIZE);
        const pageList   = Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - policyPage) <= 1)
          .reduce<(number | '…')[]>((acc, p, idx, arr) => {
            if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
            acc.push(p);
            return acc;
          }, []);

        return (
          <div className="qp-history upsell-summary__history">
            <div className="qp-history__header">
              <span className="qp-history__title upsell-summary__history-title-size">
                Policies for <strong className="upsell-summary__client-name">{selectedClient.name}</strong>
              </span>
            </div>

            <div className="qp-history__body">
              {policiesLoading ? (
                <div className="qp-history-loader upsell-summary__loader-min-height">
                  <div className="qp-history-loader__orb">
                    <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                    <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                    <div className="qp-history-loader__core"><FiSearch size={14} /></div>
                  </div>
                  <div className="qp-history-loader__text">
                    <p className="qp-history-loader__title">Loading Policies</p>
                  </div>
                </div>
              ) : policies.length === 0 ? (
                <div className="qp-empty-state">
                  <FiSearch size={28} />
                  <p>No policies found for this client</p>
                </div>
              ) : (
                <div className="qp-table-wrap glass-surface">
                  <table className="qp-table">
                    <thead>
                      <tr>
                        <th className="qp-table__th">Line of Business</th>
                        <th className="qp-table__th">Policy #</th>
                        <th className="qp-table__th">Effective</th>
                        <th className="qp-table__th">Expiration</th>
                        <th className="qp-table__th">Premium</th>
                        <th className="qp-table__th upsell-summary__text-center">Upsell Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((row, i) => (
                        <tr key={row.databaseId ?? i} className="qp-table__row">
                          <td className="qp-table__td upsell-summary__lob-cell">
                            {row.linesOfBusiness[0] || row.title || '—'}
                          </td>
                          <td className="qp-table__td">{row.policyNumber}</td>
                          <td className="qp-table__td upsell-summary__nowrap">{formatDate(row.effectiveDate)}</td>
                          <td className="qp-table__td upsell-summary__nowrap">{formatDate(row.expirationDate)}</td>
                          <td className="qp-table__td">${row.totalPremium.toLocaleString()}</td>
                          <td className="qp-table__td upsell-summary__text-center">
                            <div className="qp-table__actions">
                              <button
                                className="qp-table__action-btn"
                                title="View Upsell Summary"
                                onClick={e => { e.stopPropagation(); openSummary(row); }}
                              >
                                <img src={UpsellIcon} alt="Upsell" width={28} height={28} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {policies.length > POLICY_PAGE_SIZE && (
                <div className="qp-pagination">
                  <span className="qp-pagination__info">
                    {(policyPage - 1) * POLICY_PAGE_SIZE + 1}–{Math.min(policyPage * POLICY_PAGE_SIZE, policies.length)} of {policies.length}
                  </span>
                  <div className="qp-pagination__controls">
                    <button className="qp-pagination__btn" disabled={policyPage === 1} onClick={() => setPolicyPage(1)} title="First">«</button>
                    <button className="qp-pagination__btn" disabled={policyPage === 1} onClick={() => setPolicyPage(p => p - 1)} title="Previous">‹</button>
                    {pageList.map((p, idx) =>
                      p === '…'
                        ? <span key={`e-${idx}`} className="qp-pagination__ellipsis">…</span>
                        : <button
                            key={p}
                            className={`qp-pagination__btn${policyPage === p ? ' qp-pagination__btn--active' : ''}`}
                            onClick={() => setPolicyPage(p as number)}
                          >{p}</button>
                    )}
                    <button className="qp-pagination__btn" disabled={policyPage === totalPages} onClick={() => setPolicyPage(p => p + 1)} title="Next">›</button>
                    <button className="qp-pagination__btn" disabled={policyPage === totalPages} onClick={() => setPolicyPage(totalPages)} title="Last">»</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {!selectedClient && (
        <div className="upsell-summary__empty-wrap">
          <div className="upsell-summary__empty-inner">
            <FiSearch size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
            <p className="upsell-summary__empty-text">Search and select a client to view their policies</p>
          </div>
        </div>
      )}

      {/* Summary Modal (portal) */}
      {showDialog && ReactDOM.createPortal(
        <div
          className="csams-overlay upsell-summary__modal-overlay"
          onClick={closeDialog}
        >
          <div
            className="upsell-summary__modal-box"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="upsell-summary__modal-header">
              <div className="upsell-summary__modal-header-left">
                <FiTrendingUp size={15} style={{ color: 'var(--accent-purple)' }} />
                <span className="upsell-summary__modal-title">
                  Policy Upsell Summary
                  {activePolicy && (
                    <span className="upsell-summary__modal-title-suffix">
                      — {activePolicy.linesOfBusiness[0] || activePolicy.title}
                    </span>
                  )}
                </span>
              </div>
              <div className="upsell-summary__modal-header-right">
                {!isStreaming && !dialogLoading && summaryId && (
                  <button
                    onClick={openFeedback}
                    className="upsell-summary__feedback-btn"
                  >
                    Feedback
                  </button>
                )}
                <button
                  onClick={closeDialog}
                  className="upsell-summary__close-btn"
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div ref={contentRef} className="upsell-summary__modal-body">
              {dialogLoading && !streamText ? (
                <div className="upsell-summary__loading-row">
                  <div className="upsell-summary__spinner" />
                  <span className="upsell-summary__loading-text">Analyzing policy…</span>
                </div>
              ) : (
                <>
                  {/* Upsell Indicator badge */}
                  {summaryData?.upsell_indicator && (
                    <div className="mb-3 upsell-summary__indicator-wrap">
                      <span className="fw-bold">Upsell Indicator: </span>
                      <span
                        className={`badge ${
                          summaryData.upsell_indicator.toLowerCase() === 'yes'
                            ? 'bg-success'
                            : 'bg-secondary'
                        }`}
                      >
                        {summaryData.upsell_indicator}
                      </span>
                    </div>
                  )}

                  {/* Overall Recommendation */}
                  {summaryData?.overall_recommendation && (
                    <div
                      className="alert alert-info mb-3 upsell-summary__recommendation"
                    >
                      <strong>Overall Recommendation:</strong>
                      <p className="mb-0 mt-2">{summaryData.overall_recommendation}</p>
                    </div>
                  )}

                  {/* Streaming markdown */}
                  <div className="upsell-summary__markdown-box">
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw]}
                      remarkPlugins={[remarkGfm]}
                      components={mdTableComponents}
                    >
                      {streamText.replace(/\$\$POLICY_UPSELL_INDICATOR/g, '')}
                    </ReactMarkdown>
                    {isStreaming && (
                      <span className="upsell-summary__cursor-blink">
                        &nbsp;
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Feedback Popup */}
      {summaryId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup upsell-summary__feedback-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
          onClick={e => e.stopPropagation()}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => { setFeedbackText(e.target.value); setFeedBackError(''); }}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedback}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body,
      )}

      <style>{`
        @keyframes upsell-view-spin  { to { transform: rotate(360deg); } }
        @keyframes upsell-view-blink { 50% { opacity: 0; } }
      `}</style>
    </div>
  );
};

export default UpsellSummaryView;

